// src/components/BoardPack/components/charts/NewChartComponent.jsx
import { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import HeadingComponent from "../shared/heading";
Chart.register(...registerables);

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const BarIndicator = ({ color }) => (
  <span style={{ display:"inline-flex", alignItems:"center", marginRight:4, verticalAlign:"middle" }}>
    <span style={{ display:"inline-block", width:14, height:8, backgroundColor:color, borderRadius:1 }} />
  </span>
);

const LineIndicator = ({ color }) => (
  <span style={{ display:"inline-flex", alignItems:"center", marginRight:4, verticalAlign:"middle", position:"relative", width:20, height:10 }}>
    <span style={{ position:"absolute", top:"50%", left:0, right:0, height:2, backgroundColor:color, transform:"translateY(-50%)" }} />
    <span style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:6, height:6, borderRadius:"50%", backgroundColor:color }} />
  </span>
);

// ── Dynamic Y-axis bounds ──────────────────────────────────────────────────
// Rounds max up to a "nice" step and adds headroom for labels
const getDynamicYBounds = (allVals) => {
  if (!allVals.length) return { min: 0, max: 100000, stepSize: 20000 };

  const maxVal = Math.max(...allVals);
  if (maxVal === 0) return { min: 0, max: 10, stepSize: 2 };

  const rawStep = maxVal / 5; // aim for ~5 ticks
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const niceMultipliers = [1, 2, 2.5, 5, 10];
  const stepSize =
    niceMultipliers.map((f) => f * magnitude).find((s) => s >= rawStep) ??
    10 * magnitude;

  // Ceil to next step, then add 1 extra step as label headroom
  const roundedMax = Math.ceil((maxVal * 1.12) / stepSize) * stepSize;

return { min: 0, max: roundedMax, stepSize };
};

const NewChartComponent = ({
  title       = "",
  subtitle    = "",
  chartTitle  = "",
  yAxisLabel  = "TRANSACTIONS",
  bottomLabel = "MONTHS",
  chartData   = {},   // { "2023": { Jan: 86471, ... }, "2024": {...}, "2025": {...} }
  lineYear    = "",   // latest year shown as line
  colors      = {},   // { bar1, bar2, line }
}) => {
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const labelColRef = useRef(null);

  const years    = Object.keys(chartData).sort();
  const lineYr   = lineYear || years[years.length - 1];
  const barYears = years.filter((y) => y !== lineYr);

  const BAR_COLORS = [
    colors?.bar1 ?? "#5096D6",
    colors?.bar2 ?? "#F57F33",
  ];
  const LINE_COLOR = colors?.line ?? "#FFC000";

  const getArray = (yr) =>
    MONTHS.map((m) => {
      const v = chartData[yr]?.[m];
      return v != null ? (typeof v === "string" ? parseFloat(v.replace(/,/g, "")) : v) : null;
    });

  const lineArray = getArray(lineYr);

  const tableRows = [
    ...barYears.map((yr, idx) => ({ year: yr, color: BAR_COLORS[idx] ?? "#888", type: "bar",  values: getArray(yr) })),
    { year: lineYr, color: LINE_COLOR, type: "line", values: lineArray },
  ];

  // ── Compute dynamic bounds from ALL data ──
  const allVals = years.flatMap((yr) => getArray(yr)).filter((v) => v != null);
  const { min: yMin, max: yMax, stepSize } = getDynamicYBounds(allVals);

  useEffect(() => {
    if (!chartRef.current || !years.length) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");

    // ✅ alignTablePlugin — keeps the table's label column + total width lined up
    // with the chart's real plot-area pixels (measured from the actual canvas),
    // instead of relying on a guessed fixed width.
    const alignTablePlugin = {
      id: "alignTable",
      afterLayout(chart) {
        const area = chart.chartArea;
        if (!area || !containerRef.current || !tableRef.current) return;

        const canvasRect = containerRef.current.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        const targetLeft = canvasRect.left + area.left;
        const targetRight = canvasRect.left + area.right;

        const labelWidth = Math.max(60, targetLeft - tableRect.left);
        const totalWidth = labelWidth + (targetRight - targetLeft);

        tableRef.current.style.width = `${totalWidth}px`;
        if (labelColRef.current) {
          labelColRef.current.style.width = `${labelWidth}px`;
        }
      },
    };

    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: MONTHS,
        datasets: [
          ...barYears.map((yr, idx) => ({
            label: yr,
            data: getArray(yr),
            backgroundColor: BAR_COLORS[idx] ?? "#888",
            barPercentage: 1.0,
            categoryPercentage: 0.55,
            order: 2,
          })),
          {
            label: lineYr,
            data: lineArray,
            type: "line",
            borderColor: LINE_COLOR,
            backgroundColor: "transparent",
            pointBackgroundColor: LINE_COLOR,
            pointBorderColor: LINE_COLOR,
            pointRadius: 0,
            pointHoverRadius: 0,
            borderWidth: 4,
            tension: 0,
            spanGaps: false,
            order: 1,
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        animation: { duration: 800 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.8)",
            titleColor: "#fff",
            bodyColor: "#ccc",
            padding: 10,
            callbacks: {
              label: (c) => `${c.dataset.label}: ${c.raw?.toLocaleString() ?? "N/A"}`,
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { display: false }, offset: true, border: { display: false } },
          y: {
            min: 0,
            max: yMax,           // ✅ dynamic
            ticks: {
              color: "#ccc",
              font: { size: 11 },
              stepSize: stepSize,          // ✅ dynamic
              callback: (v) => v.toLocaleString(),
            },
            grid: { display: false },
             drawOnChartArea: false ,
            title: { display: true, text: yAxisLabel, color: "#aaa", font: { size: 12, weight: "bold" } },
          },
        },
        layout: {
          padding: { left: 26, right: 20, top: 0, bottom: 0 },
        },
      },
      plugins: [{
  id: "lineValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx: c } = chart;
    chart.data.datasets.forEach((ds, i) => {
      if (ds.type !== "line") return;
      const meta = chart.getDatasetMeta(i);
      meta.data.forEach((point, idx) => {
        const val = ds.data[idx];
        if (val == null) return;
        c.save();
        c.font = "bold 10px";
        c.fillStyle = "#ffffff"; // 👈 white color
        c.textAlign = "center";
        c.fillText(val.toLocaleString(), point.x, point.y - 10);
        c.restore();
      });
    });
  },
}, alignTablePlugin],
    });

  if (document?.fonts?.ready) {
    document.fonts.ready.then(() => {
      if (chartInstance.current) chartInstance.current.update();
    });
  }

  const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstance.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartInstance.current.resize(w, h);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  chartInstance.current?.destroy();
};
  }, [chartData, lineYr, yAxisLabel, yMin, yMax, stepSize]);

  if (!years.length) return null;

  return (
    <div className="w-full h-full flex items-center justify-center px-4">
      <div
        className="w-full mx-auto px-8 py-8 rounded-lg shadow-lg bg-card"
        style={{ backgroundColor: "#ffffff" }}
      >
        {/* Header Section */}
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "0.1rem 2rem 0.25rem 2rem",
          }}
        >
          {title && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"main"}>{title}</HeadingComponent>
            </div>
          )}
          {subtitle && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
            </div>
          )}
        </div>

        {/* Chart + Table */}
        <div
          className="w-full p-3"
          style={{ backgroundColor: "#464647", borderRadius: "8px" }}
        >
          {chartTitle && (
            <div className="text-center mt-2">
              <h3
                className="text-xs font-semibold tracking-wider"
                style={{ color: "#ffffff" }}
              >
                {chartTitle}
              </h3>
            </div>
          )}

          <div
            className="ml-3 h-[350px] relative"
            ref={containerRef}
            style={{ marginBottom: "0px" }}
          >
            <canvas ref={chartRef}></canvas>
          </div>

          <div className="overflow-x-auto">
            <table ref={tableRef} className="!border-collapse text-xs table-fixed">
              <colgroup>
                <col ref={labelColRef} style={{ width: 80 }} />
                {MONTHS.map((_, idx) => (
                  <col key={idx} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="!border-r py-0.5 px-1 text-center font-normal"
                    style={{ borderColor: "#ffffff" }}
                  ></th>
                  {MONTHS.map((m) => (
                    <th
                      key={m}
                      className="!border-r !border-t px-0.5 py-0.5 text-center font-normal text-[8px]"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map(({ year, color, type, values }) => (
                  <tr key={year}>
                    <td
                      className="!border-l !border-r !border-b px-1 py-0.5 font-normal text-[7px] text-left whitespace-nowrap overflow-hidden text-ellipsis"
                      style={{ color: "#ffffff", borderColor: "#ffffff", maxWidth: 0 }}
                      title={String(year)}
                    >
                      {type === "bar" ? <BarIndicator color={color} /> : <LineIndicator color={color} />}
                      {year}
                    </td>
                    {values.map((v, i) => (
                      <td
                        key={i}
                        className="!border-r !border-b px-0.5 py-[1px] text-center text-[8px]"
                        style={{ color: "#ffffff", borderColor: "#ffffff" }}
                      >
                        {v != null ? v.toLocaleString() : "0"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              className="text-center py-1 text-[9px]"
              style={{ color: "#ffffff" }}
            >
              {bottomLabel}
            </div>

            <div className="flex justify-center items-center gap-8 pb-3">
              {tableRows.map(({ year, color, type }) => (
                <div key={year} className="flex items-center gap-2">
                  {type === "bar" ? <BarIndicator color={color} /> : <LineIndicator color={color} />}
                  <span className="text-xs font-semibold" style={{ color: "#d1d5db" }}>{year}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewChartComponent;